import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Bookmark, Trash2, Palette } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { fetchPresets, createPreset, removePreset, DEFAULT_BUTTON_STYLE } from './buttonPresets';

// Reusable "saved button styles" bar. Works with a NESTED style object.
//   currentStyle : nested style of the button being edited (for "Save as preset")
//   onApply      : (nestedStyle) => void  — called when a preset is picked
const ButtonPresetsBar = ({ currentStyle, onApply }) => {
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState('none');
  const [mode, setMode] = useState('idle'); // idle | save | confirmDelete
  const [name, setName] = useState('');

  const load = async () => setPresets(await fetchPresets());
  useEffect(() => { load(); }, []);

  const selected = presets.find((p) => p.preset_id === selectedId);

  const handleSelect = (value) => {
    setSelectedId(value);
    setMode('idle');
    if (value === 'none') {
      onApply?.(DEFAULT_BUTTON_STYLE);
      toast.success('Reset to default button');
      return;
    }
    const preset = presets.find((p) => p.preset_id === value);
    if (preset) {
      onApply?.(preset.style || {});
      toast.success(`Applied "${preset.name}"`);
    }
  };

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error('Enter a preset name'); return; }
    try {
      const res = await createPreset(n, currentStyle || {});
      toast.success(`Saved button style "${n}"`);
      setName('');
      setMode('idle');
      await load();
      if (res?.data?.preset_id) setSelectedId(res.data.preset_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save preset');
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    try {
      await removePreset(selected.preset_id);
      setSelectedId('none');
      setMode('idle');
      load();
      toast.success('Preset deleted');
    } catch (err) {
      toast.error('Failed to delete preset');
    }
  };

  return (
    <div className="mb-2 p-2 rounded-lg border border-indigo-100 bg-indigo-50/40">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Palette size={12} className="text-indigo-500" />
        <span className="text-[11px] font-semibold text-indigo-600">Saved button styles</span>
      </div>

      {mode === 'confirmDelete' && selected ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-700">Delete “{selected.name}” completely?</span>
          <button
            type="button"
            data-testid="preset-delete-confirm"
            onClick={confirmDelete}
            className="px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 text-[11px] font-medium"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setMode('idle')}
            className="px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 text-[11px]"
          >
            Cancel
          </button>
        </div>
      ) : mode === 'save' ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            placeholder="Name this style"
            className="h-8 text-xs w-40"
            data-testid="preset-name-input"
          />
          <button
            type="button"
            onClick={save}
            data-testid="preset-save-confirm"
            className="px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-[11px] font-medium"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => { setMode('idle'); setName(''); }}
            className="px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 text-[11px]"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedId} onValueChange={handleSelect}>
            <SelectTrigger className="h-8 text-xs w-48" data-testid="preset-select">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {presets.map((p) => (
                <SelectItem key={p.preset_id} value={p.preset_id} data-testid={`preset-option-${p.preset_id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            type="button"
            data-testid="preset-delete-btn"
            disabled={!selected}
            onClick={() => setMode('confirmDelete')}
            title={selected ? `Delete "${selected.name}"` : 'Select a preset to delete'}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 bg-white text-slate-400 hover:text-red-500 hover:border-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 size={14} />
          </button>

          <button
            type="button"
            data-testid="save-preset-btn"
            onClick={() => setMode('save')}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 text-[11px] font-medium transition-colors"
          >
            <Bookmark size={12} /> Save as preset
          </button>
        </div>
      )}
    </div>
  );
};

export default ButtonPresetsBar;
